import { Ship, Home } from 'lucide-react'
import { useMemo } from 'react'
import { Badge } from '@/components/ui/badge'
import { useCltWizardStore } from '@/stores/useCltWizardStore'
import { useMilestones } from '@/hooks/useCltReference'
import { CltStepHeader } from './CltStepHeader'
import {
  SUBSTEPS,
  labelOf,
  summarizeRule,
  type CltMatrix,
  type Labeled,
  type StrategyGroup,
} from './clt.mock'

function matrixEntries(
  matrix: CltMatrix,
  strategyGroups: StrategyGroup[],
  milestones: Labeled[]
) {
  return Object.entries(matrix).map(([key, rule]) => {
    const [milestoneId, strategyId] = key.split('__')
    return {
      key,
      milestone: labelOf(milestones, milestoneId),
      strategy: labelOf(strategyGroups, strategyId),
      summary: summarizeRule(rule, milestones),
    }
  })
}

function MatrixSummary({
  icon,
  title,
  matrix,
  strategyGroups,
  milestones,
}: {
  icon: React.ReactNode
  title: string
  matrix: CltMatrix
  strategyGroups: StrategyGroup[]
  milestones: Labeled[]
}) {
  const entries = matrixEntries(matrix, strategyGroups, milestones)
  return (
    <div className="rounded-md border border-border">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        {icon}
        <span className="text-sm font-medium text-foreground">{title}</span>
        <Badge variant="secondary" className="px-1.5 py-0 text-[11px]">
          {entries.length} rule{entries.length === 1 ? '' : 's'}
        </Badge>
      </div>
      {entries.length === 0 ? (
        <p className="px-3 py-2 text-xs text-muted-foreground">
          No rules configured.
        </p>
      ) : (
        <ul className="max-h-[160px] divide-y divide-border/60 overflow-y-auto">
          {entries.map(entry => (
            <li
              key={entry.key}
              className="flex items-center justify-between gap-3 px-3 py-1.5 text-xs"
            >
              <span className="font-medium text-foreground">
                {entry.milestone} · {entry.strategy}
              </span>
              <span className="text-muted-foreground">{entry.summary}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function SummaryRow({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-right text-sm font-medium text-foreground">
        {children}
      </span>
    </div>
  )
}

export function StepReview() {
  const name = useCltWizardStore(s => s.name)
  const strategyGroups = useCltWizardStore(s => s.strategyGroups)
  const matrices = useCltWizardStore(s => s.matrices)
  const biweeklyOffsetGroups = useCltWizardStore(s => s.biweeklyOffsetGroups)
  const appliesToWeeks = useCltWizardStore(s => s.appliesToWeeks)
  const isDefault = useCltWizardStore(s => s.isDefault)
  const { data: milestoneList = [] } = useMilestones()

  // See MatrixGrid: backend stores milestone label in rule milestone_id; use
  // the label as the local id so summarizeRule's milestone lookups resolve.
  const milestones: Labeled[] = useMemo(
    () => milestoneList.map(m => ({ id: m.label, label: m.label })),
    [milestoneList]
  )
  return (
    <div className="mx-auto w-full max-w-[820px]">
      <CltStepHeader
        title="Review"
        description="Review everything before saving."
      />

      {/* Review summary */}
      <div className="space-y-4 rounded-lg border border-border bg-card p-5">
        <div className="divide-y divide-border/60">
          <SummaryRow label="Template name">
            {name.trim() || <span className="text-destructive">Not set</span>}
          </SummaryRow>
          <SummaryRow label="Default template">
            {isDefault ? 'Yes' : 'No'}
          </SummaryRow>
          <SummaryRow label="Strategy groups">
            <div className="flex flex-col items-end gap-0.5">
              {strategyGroups.map(g => (
                <div key={g.id}>
                  <span className="font-semibold">{g.label}</span>
                  <span className="text-muted-foreground">
                    {' — '}
                    {g.codes.length > 0
                      ? `codes ${g.codes.join(', ')}`
                      : 'no codes'}
                  </span>
                </div>
              ))}
            </div>
          </SummaryRow>
          <SummaryRow label="Biweekly offsets">
            {biweeklyOffsetGroups.length === 0 ? (
              'None configured'
            ) : (
              <div className="flex flex-col items-end gap-2">
                {biweeklyOffsetGroups.map(g => (
                  <div key={g.id} className="flex flex-col items-end gap-0.5">
                    <div>
                      <span className="font-semibold">{g.label}</span>
                      <span className="text-muted-foreground">
                        {' — '}
                        {g.offsetDays}d · {g.milestoneIds.length} milestone
                        {g.milestoneIds.length === 1 ? '' : 's'} ·{' '}
                        {g.targets.length} target
                        {g.targets.length === 1 ? '' : 's'}
                      </span>
                    </div>
                    {g.targets.map(t => {
                      const groupLabel =
                        strategyGroups.find(sg => sg.id === t.strategyGroupId)
                          ?.label ?? '—'
                      return (
                        <div
                          key={t.id}
                          className="text-xs text-muted-foreground"
                        >
                          {groupLabel} · {t.isImport ? 'Import' : 'Domestic'} ·{' '}
                          {t.isRail ? 'Rail' : 'No Rail'}
                        </div>
                      )
                    })}
                  </div>
                ))}
              </div>
            )}
          </SummaryRow>
          <SummaryRow label="Applies to weeks">
            {appliesToWeeks.length === 0
              ? 'None selected'
              : `${appliesToWeeks.filter(w => w <= 52).length} of 52${
                  appliesToWeeks.includes(53) ? ' + W53*' : ''
                }`}
          </SummaryRow>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {SUBSTEPS.map(sub => (
            <MatrixSummary
              key={sub.key}
              icon={
                sub.group === 'Import' ? (
                  <Ship className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Home className="h-4 w-4 text-muted-foreground" />
                )
              }
              title={sub.label}
              matrix={matrices[sub.key]}
              strategyGroups={strategyGroups}
              milestones={milestones}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
